import React from 'react';
import { Tag, Popover, Tooltip } from 'antd';
import { HtmlContent } from '@client/utils/html_content';

/**
 * Strips HTML tags from string for tooltip/plain text display
 */
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Collapsible tags component for select components.
 * Shows a limited number of visible tags with a "+N" popover for the rest.
 * Supports HTML content in labels.
 *
 * @param {Object} props
 * @param {Array} props.value - Selected values
 * @param {Function} props.getLabel - Function to get label for a value
 * @param {Function} props.onRemove - Remove handler
 * @param {number} [props.maxVisible=1] - Maximum number of tags to show before collapsing
 */
const CollapsibleTags = ({ value, getLabel, onRemove, maxVisible = 1 }) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  const visibleItems = value.slice(0, maxVisible);
  const remainingItems = value.slice(maxVisible);
  const hasMore = remainingItems.length > 0;

  const renderTag = (val) => {
    const rawLabel = String(getLabel(val) || val);
    const plainLabel = stripHtml(rawLabel);
    const truncatedLabel = plainLabel.length > 20
      ? `${plainLabel.substring(0, 20)}...`
      : plainLabel;
    return (
      <Tooltip title={plainLabel} key={val}>
        <Tag
          closable
          onClose={() => onRemove(val)}
          className="selected-tag"
        >
          {truncatedLabel}
        </Tag>
      </Tooltip>
    );
  };

  const popoverContent = (
    <div className="flex flex-wrap gap-1" style={{ maxWidth: '300px' }}>
      {remainingItems.map((val) => {
        const rawLabel = String(getLabel(val) || val);
        return (
          <Tag
            key={val}
            closable
            onClose={() => onRemove(val)}
          >
            <HtmlContent html={rawLabel} className="inline" />
          </Tag>
        );
      })}
    </div>
  );

  return (
    <div className="selected-tags-container flex items-center flex-wrap gap-1 mt-2">
      {visibleItems.map((val) => renderTag(val))}
      {hasMore && (
        <Popover content={popoverContent} trigger={['hover', 'click']} placement="bottom">
          <Tag className="cursor-pointer">
            +{remainingItems.length}
          </Tag>
        </Popover>
      )}
    </div>
  );
};

export default CollapsibleTags;
